import {
  Autocomplete,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography,
  Box,
  Fade,
  useTheme,
  alpha,
  Alert,
  AlertTitle,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import React, { useMemo } from "react";

// Create a memoized Autocomplete component to reduce re-renders
const MemoizedAutocomplete = React.memo(
  ({ value, onChange, options, label, placeholder }) => (
    <Autocomplete
      value={value}
      onChange={onChange}
      options={options}
      getOptionLabel={(opt) => opt.label}
      isOptionEqualToValue={(opt, val) => opt.value === val.value}
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          variant="outlined"
          sx={{ "& .MuiOutlinedInput-root": { borderRadius: 1.5 } }}
        />
      )}
      fullWidth
    />
  )
);

MemoizedAutocomplete.displayName = "MemoizedAutocomplete";

const ManualEntryDialog = React.memo(
  ({
    open,
    onClose,
    category,
    task,
    manualBoxNo,
    setManualBoxNo,
    filteredOptions,
    onSave,
  }) => {
    const theme = useTheme();
    const isModify = task.isOk;

    // Memoize the container type calculation
    const actualContainerType = useMemo(() => {
      return filteredOptions.length > 0
        ? filteredOptions[0].label.split(" ")[0] // Get the first word (e.g., "Bowl")
        : category;
    }, [filteredOptions, category]);

    // Memoize the autocomplete value
    const autocompleteValue = useMemo(() => {
      return filteredOptions.find((opt) => opt.value === manualBoxNo) || null;
    }, [filteredOptions, manualBoxNo]);

    // Memoize labels and placeholders
    const autocompleteProps = useMemo(
      () => ({
        label: `Available ${actualContainerType} Containers`,
        placeholder: `Choose a ${actualContainerType?.toLowerCase()} container...`,
      }),
      [actualContainerType]
    );

    const handleAutocompleteChange = (_, newValue) => {
      setManualBoxNo(newValue?.value || null);
    };

    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="sm"
        fullWidth
        TransitionComponent={Fade}
        PaperProps={{
          elevation: 4,
          sx: { borderRadius: 2 },
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            borderBottom: `1px solid ${theme.palette.divider}`,
            pb: 1.5,
            color: isModify
              ? theme.palette.primary.main
              : theme.palette.text.primary,
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          <InfoOutlinedIcon fontSize="small" />
          {isModify
            ? `Modify ${actualContainerType}`
            : `Select ${actualContainerType} Position`}
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <Alert
            severity="info"
            variant="outlined"
            sx={{
              my: 3,
              backgroundColor: alpha(theme.palette.info.light, 0.1),
            }}
          >
            <AlertTitle>{task.item_name} container selection</AlertTitle>
            {task.item_name && (
              <>
                Please select which {actualContainerType?.toLowerCase()}{" "}
                container to use for{" "}
                <Box component="span" fontWeight="bold">
                  {task.item_name}
                </Box>
                <br />
                <br />
              </>
            )}
            Note: This will only apply to the current recipe and won't update
            your kitchen inventory. <br />
          </Alert>

          <Typography
            variant="body1"
            sx={{ mb: 3, color: theme.palette.text.secondary }}
          >
            Please select from the available{" "}
            {actualContainerType?.toLowerCase()} containers:
            <br />
          </Typography>

          <MemoizedAutocomplete
            value={autocompleteValue}
            onChange={handleAutocompleteChange}
            options={filteredOptions}
            label={autocompleteProps.label}
            placeholder={autocompleteProps.placeholder}
          />
        </DialogContent>
        <DialogActions
          sx={{ p: 2, borderTop: `1px solid ${theme.palette.divider}` }}
        >
          <Button
            onClick={onClose}
            color="inherit"
            variant="outlined"
            sx={{ borderRadius: 1.5, textTransform: "none", fontWeight: 600 }}
          >
            Cancel
          </Button>
          <Button
            onClick={onSave}
            color="primary"
            variant="contained"
            disabled={manualBoxNo === null}
            sx={{
              borderRadius: 1.5,
              textTransform: "none",
              fontWeight: 600,
              px: 2,
            }}
          >
            {isModify ? "Update" : "Confirm Selection"}
          </Button>
        </DialogActions>
      </Dialog>
    );
  }
);

ManualEntryDialog.displayName = "ManualEntryDialog";

export default ManualEntryDialog;
